export interface DownloadOptions {
  filename?: string;
  showProgress?: boolean;
  onProgress?: (progress: number) => void;
  onStart?: () => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

// Helper to get MIME type from file extension
const getMimeTypeFromExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const mimeTypes: Record<string, string> = {
    // Documents
    'pdf': 'application/pdf',
    'doc': 'application/msword',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'csv': 'text/csv',
    'ppt': 'application/vnd.ms-powerpoint',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'txt': 'text/plain',
    'rtf': 'application/rtf',
    // OpenDocument formats
    'odt': 'application/vnd.oasis.opendocument.text',
    'ods': 'application/vnd.oasis.opendocument.spreadsheet',
    'odp': 'application/vnd.oasis.opendocument.presentation',
    // Images
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp',
    'svg': 'image/svg+xml',
    // Video
    'mp4': 'video/mp4',
    'webm': 'video/webm',
    'mov': 'video/quicktime',
    // Audio
    'mp3': 'audio/mpeg',
    'wav': 'audio/wav',
    'm4a': 'audio/mp4',
    // Archives
    'zip': 'application/zip',
    // eBooks
    'epub': 'application/epub+zip',
  };
  return mimeTypes[ext] || 'application/octet-stream';
};

/**
 * Downloads a file from a URL with progress tracking and error handling
 */
export const downloadFile = async (
  url: string,
  options: DownloadOptions = {}
): Promise<void> => {
  const {
    filename,
    showProgress = false,
    onProgress,
    onStart,
    onComplete,
    onError
  } = options;

  try {
    onStart?.();

    // Fetch the file
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
    }

    // Get filename from options, response headers, or URL
    let downloadFilename = filename;
    if (!downloadFilename) {
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          downloadFilename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Fallback to URL pathname
      if (!downloadFilename) {
        const urlPath = new URL(url).pathname;
        downloadFilename = urlPath.split('/').pop() || 'download';
      }
    }

    // Use response.blob() directly - this preserves the file correctly
    const blob = await response.blob();

    // If blob type is empty or generic, create new blob with correct MIME type
    let finalBlob = blob;
    if (!blob.type || blob.type === 'application/octet-stream') {
      const contentType = getMimeTypeFromExtension(downloadFilename || '');
      finalBlob = new Blob([blob], { type: contentType });
    }

    // Report progress as complete (since we used blob() which doesn't support streaming progress)
    if (showProgress && onProgress) {
      onProgress(100);
    }

    // Create download link and trigger download
    const downloadUrl = URL.createObjectURL(finalBlob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFilename!;

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL after a short delay
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);

    onComplete?.();

  } catch (error) {
    const downloadError = error instanceof Error ? error : new Error('Unknown download error');
    onError?.(downloadError);
    throw downloadError;
  }
};

/**
 * Downloads multiple files sequentially
 */
export const downloadMultipleFiles = async (
  downloads: { url: string; filename?: string }[],
  options: Omit<DownloadOptions, 'filename'> = {}
): Promise<void> => {
  const { onProgress, onStart, onComplete, onError } = options;
  
  try {
    onStart?.();
    
    for (let i = 0; i < downloads.length; i++) {
      const { url, filename } = downloads[i];
      
      await downloadFile(url, {
        filename,
        onError,
        onProgress: (fileProgress) => {
          if (!onProgress) return;
          const overallProgress = Math.round(((i + fileProgress / 100) / downloads.length) * 100);
          onProgress(overallProgress);
        }
      });
      
      // Small delay between downloads to prevent overwhelming the browser
      if (i < downloads.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    onComplete?.();
    
  } catch (error) {
    const downloadError = error instanceof Error ? error : new Error('Multiple download error');
    onError?.(downloadError);
    throw downloadError;
  }
};

/**
 * Creates a downloadable link for data (useful for generated content)
 */
export const downloadData = (
  data: string | Blob | ArrayBuffer,
  filename: string,
  mimeType = 'application/octet-stream'
): void => {
  let blob: Blob;
  
  if (data instanceof Blob) {
    blob = data;
  } else if (data instanceof ArrayBuffer) {
    blob = new Blob([data], { type: mimeType });
  } else {
    blob = new Blob([data], { type: mimeType });
  }
  
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Validates if a URL is downloadable
 */
export const isDownloadableUrl = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Gets file info without downloading
 */
export const getFileInfo = async (url: string): Promise<{
  size?: number;
  type?: string;
  filename?: string;
}> => {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    
    if (!response.ok) {
      throw new Error(`Failed to get file info: ${response.status}`);
    }
    
    const contentLength = response.headers.get('Content-Length');
    const contentType = response.headers.get('Content-Type');
    const contentDisposition = response.headers.get('Content-Disposition');
    
    let filename: string | undefined;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    return {
      size: contentLength ? parseInt(contentLength, 10) : undefined,
      type: contentType || undefined,
      filename
    };
    
  } catch (error) {

    return {};
  }
};

